import React, { useEffect, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { useDealActions, useFileMetaSelector } from "store";
import { IFile } from "store/types";

interface Props {
  file: Pick<IFile, "name" | "type" | "created_at">;
  onShare?: () => void;
  onDownload?: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const FileInfoPrompt: React.FC<Props> = ({ file, onShare, onDownload, onSubmit, onCancel }) => {
  const { invalidateFileMetaAction } = useDealActions();
  const { name, type, location, owner, upload_date } = useFileMetaSelector();
  const format = useFormatDateHook();

  useEffect(() => {
    return () => {
      invalidateFileMetaAction();
    };
  }, [invalidateFileMetaAction]);

  const displayLocation = useMemo(() => {
    if (Array.isArray(location)) return location.join(" > ");
    if (typeof location === "string") return location;
    return "-";
  }, [location]);

  const displayType = (type || file.type || "-").toString().toUpperCase();
  const displayName = name || file.name || "File";
  const displayOwner = owner || "-";
  const displayUploadRaw = upload_date || file.created_at;
  const displayUpload = displayUploadRaw ? format({ date: displayUploadRaw }) : "-";

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "520px",
        maxWidth: "calc(100vw - 64px)",
        p: "32px",
        borderRadius: "8px",
        bgcolor: "white",
        gap: "20px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: "1px solid #DBE8F5", pb: "8px" }}
      >
        <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#071F35" }}>File info</Typography>
        <Stack direction="row" alignItems="center" gap="6px">
          {(onShare || onDownload) && (
            <Stack direction="row" alignItems="center" gap="6px">
              {onShare && (
                <IconButton onClick={onShare} sx={{ p: "4px", borderRadius: "32px" }}>
                  <MaterialTooltip title="Share" isShow>
                    <PersonAddOutlinedIcon sx={{ fontSize: "20px", color: "#071F35" }} />
                  </MaterialTooltip>
                </IconButton>
              )}
              {onDownload && (
                <IconButton onClick={onDownload} sx={{ p: "4px", borderRadius: "32px" }}>
                  <MaterialTooltip title="Download" isShow>
                    <FileDownloadOutlinedIcon sx={{ fontSize: "20px", color: "#071F35" }} />
                  </MaterialTooltip>
                </IconButton>
              )}
              <Box sx={{ width: "1px", height: "24px", bgcolor: "#DBE8F5" }} />
            </Stack>
          )}
          <IconButton onClick={onCancel} sx={{ p: "4px", borderRadius: "32px" }}>
            <CloseIcon sx={{ fontSize: "20px", color: "#071F35" }} />
          </IconButton>
        </Stack>
      </Stack>

      <Stack gap="4px" sx={{ color: "#071F35" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 500, lineHeight: "24px" }}>{displayName}</Typography>
        <Typography sx={{ fontSize: "16px", lineHeight: "24px" }}>
          <Box component="span" sx={{ color: "#496682" }}>
            Type:{" "}
          </Box>
          <Box component="span">{displayType}</Box>
        </Typography>
        <Typography sx={{ fontSize: "16px", lineHeight: "24px" }}>
          <Box component="span" sx={{ color: "#496682" }}>
            Location:{" "}
          </Box>
          <Box component="span">{displayLocation}</Box>
        </Typography>
        <Typography sx={{ fontSize: "16px", lineHeight: "24px" }}>
          <Box component="span" sx={{ color: "#496682" }}>
            Owner:{" "}
          </Box>
          <Box component="span">{displayOwner}</Box>
        </Typography>
        <Typography sx={{ fontSize: "16px", lineHeight: "24px" }}>
          <Box component="span" sx={{ color: "#496682" }}>
            Upload:{" "}
          </Box>
          <Box component="span">{displayUpload}</Box>
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        <Button
          onClick={onSubmit}
          variant="outlined"
          sx={{
            borderRadius: "32px",
            borderColor: "#7B92A9",
            px: "16px",
            py: "4px",
            color: "#071F35",
            textTransform: "none"
          }}
        >
          OK
        </Button>
      </Stack>
    </Stack>
  );
};
